import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import {
  AbstractControl,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import alertify from 'alertifyjs';
import { LandingNavbarComponent } from '../../components/landing-navbar/landing-navbar.component';
import { ApiServiceService } from '../../modules/services/api-service.service';
import { SetPasswordRequest } from '../registration-page/registration-model';

const passwordMatchValidator = (
  control: AbstractControl,
): ValidationErrors | null => {
  const password = control.get('password')?.value;
  const confirmPassword = control.get('confirmPassword')?.value;

  if (!password || !confirmPassword) return null;

  return password === confirmPassword ? null : { passwordMismatch: true };
};

const strongPasswordValidator = (
  control: AbstractControl,
): ValidationErrors | null => {
  const value = control.value as string;
  if (!value) return null;

  const errors: Record<string, boolean> = {};

  if (value.length < 8) errors['minlength'] = true;
  if (!/[A-Z]/.test(value)) errors['uppercase'] = true;
  if (!/[a-z]/.test(value)) errors['lowercase'] = true;
  if (!/\d/.test(value)) errors['number'] = true;
  if (!/[^A-Za-z0-9]/.test(value)) errors['specialCharacter'] = true;

  return Object.keys(errors).length ? errors : null;
};

type SetPasswordResponse = {
  status?: string;
  message?: string;
};

@Component({
  selector: 'app-create-password-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    LandingNavbarComponent,
  ],
  templateUrl: './create-password-page.html',
  styleUrl: './create-password-page.scss',
})
export class CreatePasswordPage implements AfterViewInit, OnDestroy {
  readonly createPasswordForm;
  showPassword = false;
  showConfirmPassword = false;
  isSubmitting = false;
  requestToken = '';
  private intersectionObserver: IntersectionObserver | null = null;

  constructor(
    private readonly formBuilder: NonNullableFormBuilder,
    private readonly route: ActivatedRoute,
    private readonly apiService: ApiServiceService,
    private readonly router: Router,
    private readonly host: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
  ) {
    alertify.set('notifier', 'position', 'top-right');
    alertify.set('notifier', 'delay', 4);

    this.requestToken =
      this.route.snapshot.queryParamMap.get('request_token') ||
      this.route.snapshot.queryParamMap.get('token') ||
      '';

    this.createPasswordForm = this.formBuilder.group(
      {
        password: ['', [Validators.required, strongPasswordValidator]],
        confirmPassword: ['', Validators.required],
      },
      { validators: passwordMatchValidator },
    );
  }

  ngAfterViewInit(): void {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting)
            this.renderer.addClass(entry.target, 'visible');
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' },
    );

    const revealEls =
      this.host.nativeElement.querySelectorAll('.auth-scroll-reveal');
    revealEls.forEach((el) => this.intersectionObserver?.observe(el));
  }

  ngOnDestroy(): void {
    this.intersectionObserver?.disconnect();
    this.intersectionObserver = null;
  }

  get passwordControl() {
    return this.createPasswordForm.controls.password;
  }

  get confirmPasswordControl() {
    return this.createPasswordForm.controls.confirmPassword;
  }

  get passwordErrorMessage(): string {
    if (!this.passwordControl.touched && !this.passwordControl.dirty) return '';
    if (this.passwordControl.hasError('required')) {
      return 'Password is required.';
    }

    const requirements = [
      this.passwordControl.hasError('minlength') ? 'at least 8 characters' : '',
      this.passwordControl.hasError('uppercase') ? 'one uppercase letter' : '',
      this.passwordControl.hasError('lowercase') ? 'one lowercase letter' : '',
      this.passwordControl.hasError('number') ? 'one number' : '',
      this.passwordControl.hasError('specialCharacter')
        ? 'one special character'
        : '',
    ].filter(Boolean);

    return requirements.length ? `Use ${requirements.join(', ')}.` : '';
  }

  get confirmPasswordErrorMessage(): string {
    if (
      !this.confirmPasswordControl.touched &&
      !this.confirmPasswordControl.dirty
    ) {
      return '';
    }
    if (this.confirmPasswordControl.hasError('required')) {
      return 'Confirm password is required.';
    }
    if (this.createPasswordForm.hasError('passwordMismatch')) {
      return 'Passwords do not match.';
    }
    return '';
  }

  get canSubmit(): boolean {
    return (
      !!this.requestToken && this.createPasswordForm.valid && !this.isSubmitting
    );
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  submit(): void {
    if (!this.requestToken) {
      alertify.error('This password setup link is invalid or has expired.');
      return;
    }

    if (this.createPasswordForm.invalid) {
      this.createPasswordForm.markAllAsTouched();
      alertify.error('Create a valid password before continuing.');
      return;
    }

    const formValue = this.createPasswordForm.getRawValue();
    const payload: SetPasswordRequest = {
      request_token: this.requestToken,
      password: formValue.password,
      confirm_password: formValue.confirmPassword,
    };

    this.isSubmitting = true;

    this.apiService.post('/setup-password', payload).subscribe({
      next: (response: SetPasswordResponse) => {
        if (response.status && response.status !== 'success') {
          alertify.error(response.message || 'We could not set your password.');
          this.isSubmitting = false;
          return;
        }

        alertify.success(
          response.message ||
            'Password created successfully. Sign in to continue.',
        );
        this.isSubmitting = false;
        void this.router.navigate(['/login']);
      },
      error: (error) => {
        const message =
          typeof error === 'string'
            ? error
            : 'We could not set your password. Please try again.';
        alertify.error(message);
        this.isSubmitting = false;
      },
    });
  }
}
